import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, FileText, ChevronLeft, ChevronRight, FileCheck2, Pencil, FolderOpen, Briefcase, AlertTriangle, Workflow } from "lucide-react";
import InlineEdit from "@/components/InlineEdit";
import ContratoFormDialog from "@/components/ContratoFormDialog";
import { useConfirm } from "@/components/ConfirmProvider";
import MandanteLogo from "@/components/MandanteLogo";
import WorkflowBuilder from "@/components/WorkflowBuilder";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function MandanteDetail() {
  const { id } = useParams();
  const confirmAction = useConfirm();
  const nav = useNavigate();
  const { user } = useAuth();
  const [mandante, setMandante] = useState(null);
  const [docs, setDocs] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [newDoc, setNewDoc] = useState("");
  const [newCargo, setNewCargo] = useState("");
  const [similarWarn, setSimilarWarn] = useState(null); // { name, similares: [string] }
  const [contratoDialog, setContratoDialog] = useState(null);
  const canEdit = ["ADMIN", "HR_MANAGER"].includes(user?.role);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split("-");
    return y && m && d ? `${d}-${m}-${y}` : s;
  };

  const load = async () => {
    try {
      const [m, d, cg, w] = await Promise.all([
        api.get("/mandantes").then((r) => r.data.find((x) => x.id === id)),
        api.get(`/mandantes/${id}/documentos`).then((r) => r.data),
        api.get(`/mandantes/${id}/cargos`).then((r) => r.data),
        api.get("/workers").then((r) => r.data),
      ]);
      if (!m) { toast.error("Mandante no encontrado"); nav("/mandantes"); return; }
      setMandante(m); setDocs(d); setCargos(cg); setWorkers(w);
    } catch (e) { toast.error("No se pudo cargar"); }
  };
  useEffect(() => { load(); }, [id]);

  const countContrato = (c) => workers.filter((w) => (w.mandante || w.company) === mandante?.name && w.contrato === c).length;
  const countCargo = (name) => workers.filter((w) => (w.mandante || w.company) === mandante?.name && w.position === name).length;

  // Similaridad simple basada en Levenshtein normalizado.
  const normalize = (s) => (s || "").toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ0-9 ]/g, "").trim();
  const levenshtein = (a, b) => {
    if (a === b) return 0;
    const m = a.length, n = b.length;
    if (!m) return n; if (!n) return m;
    const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
    for (let j = 1; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    return dp[m][n];
  };
  const findSimilares = (name) => {
    const n = normalize(name);
    if (!n) return [];
    return cargos.map((c) => c.name).filter((x) => {
      const nx = normalize(x);
      if (!nx || nx === n) return false;
      const dist = levenshtein(n, nx);
      const maxLen = Math.max(n.length, nx.length);
      const ratio = dist / maxLen;
      return dist <= 2 || ratio <= 0.25 || nx.includes(n) || n.includes(nx);
    });
  };

  const createCargo = async (name) => {
    try {
      const { data } = await api.post(`/mandantes/${id}/cargos`, { name });
      setCargos([...cargos, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCargo(""); toast.success("Cargo agregado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo agregar"); }
  };
  const addCargo = async () => {
    const name = (newCargo || "").trim();
    if (!name) return;
    const similares = findSimilares(name);
    if (similares.length > 0) { setSimilarWarn({ name, similares }); return; }
    await createCargo(name);
  };
  const renameCargo = async (cg, name) => {
    try {
      const { data } = await api.put(`/mandantes/${id}/cargos/${cg.id}`, { name });
      setCargos(cargos.map((c) => c.id === cg.id ? { ...c, ...data } : c).sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Cargo renombrado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo renombrar"); }
  };
  const removeCargo = async (cg) => {
    try {
      await api.delete(`/mandantes/${id}/cargos/${cg.id}`);
      setCargos(cargos.filter((c) => c.id !== cg.id)); toast.success("Cargo eliminado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  const renameMandante = async (name) => {
    const { data } = await api.put(`/mandantes/${id}`, { name });
    setMandante((s) => ({ ...s, name: data.name }));
    toast.success("Mandante renombrado");
  };
  const addContrato = async (payload) => {
    const { data } = await api.post(`/mandantes/${id}/contratos`, payload);
    setMandante((s) => ({ ...s, contratos: [...(s.contratos || []), data] }));
    toast.success("Contrato creado");
  };
  const updateContrato = async (contrato, payload) => {
    const { data } = await api.put(`/mandantes/${id}/contratos/${contrato.id}`, payload);
    setMandante((s) => ({ ...s, contratos: (s.contratos || []).map((cc) => cc.id === contrato.id ? { ...cc, ...data } : cc) }));
    toast.success("Contrato actualizado");
  };
  const removeContrato = async (e, c) => {
    e.stopPropagation();
    const ok = await confirmAction({ title: `Eliminar contrato "${c.name}"`,
      description: "Se eliminarán también sus trabajadores asociados a este contrato. Esta acción no se puede deshacer.",
      confirmText: "Eliminar contrato", variant: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/mandantes/${id}/contratos/${c.id}`);
      setMandante((s) => ({ ...s, contratos: (s.contratos || []).filter((cc) => cc.id !== c.id) }));
      toast.success("Contrato eliminado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  const addDoc = async () => {
    if (!newDoc.trim()) return;
    try {
      const { data } = await api.post(`/mandantes/${id}/documentos`, { name: newDoc });
      setDocs([...docs, data]); setNewDoc(""); toast.success("Documento agregado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo agregar"); }
  };
  const renameDoc = async (d, name) => {
    await api.put(`/mandantes/${id}/documentos/${d.id}`, { name });
    setDocs(docs.map((x) => x.id === d.id ? { ...x, name } : x));
    toast.success("Documento renombrado");
  };
  const removeDoc = async (d) => {
    await api.delete(`/mandantes/${id}/documentos/${d.id}`);
    setDocs(docs.filter((x) => x.id !== d.id)); toast.success("Documento eliminado");
  };
  const updateDocAttrs = async (d, patch) => {
    try {
      const { data } = await api.put(`/mandantes/${id}/documentos/${d.id}`, patch);
      setDocs((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...data } : x)));
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); }
  };

  if (!mandante) return <div className="text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/mandantes" className="hover:text-indigo-600 inline-flex items-center gap-1" data-testid="breadcrumb-mandantes"><ChevronLeft size={14} /> Mandantes</Link>
        <ChevronRight size={14} />
        <span className="text-slate-700 font-medium">{mandante.name}</span>
      </nav>

      <div className="flex items-center gap-3">
        <MandanteLogo mandante={mandante} size="lg" editable={canEdit}
          onChanged={(m) => setMandante((s) => ({ ...s, ...m }))} />
        <div className="flex-1 min-w-0">
          <InlineEdit testid="mandante-name" value={mandante.name} disabled={!canEdit} onSave={renameMandante}
            className="font-display text-3xl font-bold text-slate-900" />
          <p className="text-slate-500 text-sm mt-1">Configuración · {(mandante.contratos || []).length} contrato(s) · {docs.length} doc(s) requerido(s) · {cargos.length} cargo(s)</p>
        </div>
        <Link to={`/acreditacion/${id}`} data-testid="go-to-acreditacion"
          className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg">
          <FolderOpen size={14} /> Ir a acreditación
        </Link>
      </div>

      <Tabs defaultValue="contratos" className="w-full">
        <TabsList data-testid="mandante-tabs" className="bg-slate-100 h-10 p-1 rounded-xl">
          <TabsTrigger data-testid="tab-contratos" value="contratos" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700 px-4">
            <FileText size={14} className="mr-1.5" /> Contratos
          </TabsTrigger>
          <TabsTrigger data-testid="tab-documentos" value="documentos" className="data-[state=active]:bg-white data-[state=active]:text-emerald-700 px-4">
            <FileCheck2 size={14} className="mr-1.5" /> Documentos del trabajador
          </TabsTrigger>
          <TabsTrigger data-testid="tab-cargos" value="cargos" className="data-[state=active]:bg-white data-[state=active]:text-blue-700 px-4">
            <Briefcase size={14} className="mr-1.5" /> Mantenedor de cargos
          </TabsTrigger>
          <TabsTrigger data-testid="tab-workflow" value="workflow" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700 px-4">
            <Workflow size={14} className="mr-1.5" /> Timeline del proceso
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contratos" className="mt-4">
      {/* Contratos */}
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><FileText size={18} className="text-indigo-600" /> Contratos</h2>
          {canEdit && (
            <button data-testid="open-new-contrato" onClick={() => setContratoDialog({ contrato: null })}
              className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3 py-1.5 rounded-md">
              <Plus size={14} /> Nuevo contrato
            </button>
          )}
        </div>
        {(mandante.contratos || []).length === 0 ? (
          <p className="text-sm text-slate-400 italic p-5">Sin contratos registrados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="contratos-table" className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <th className="px-5 py-2 whitespace-nowrap">N° Contrato</th>
                  <th className="px-5 py-2">Glosa</th>
                  <th className="px-5 py-2 whitespace-nowrap">Inicio</th>
                  <th className="px-5 py-2 whitespace-nowrap">Término</th>
                  <th className="px-5 py-2 text-right whitespace-nowrap">Dotación</th>
                  <th className="px-5 py-2">Gerencia</th>
                  <th className="px-5 py-2 text-right whitespace-nowrap">Trab.</th>
                  {canEdit && <th className="px-5 py-2 text-right w-20"></th>}
                </tr>
              </thead>
              <tbody>
                {(mandante.contratos || []).map((c) => (
                  <tr key={c.id} data-testid={`contrato-row-${c.id}`} className="border-t border-slate-50 hover:bg-indigo-50/40 cursor-pointer transition-colors"
                    onClick={() => nav(`/mandantes/${id}/contratos/${c.id}`)}>
                    <td className="px-5 py-2.5 font-mono text-[11px] text-slate-700 whitespace-nowrap">{c.numero_contrato || <span className="text-slate-300">—</span>}</td>
                    <td className="px-5 py-2.5 font-semibold text-slate-800">{c.name}</td>
                    <td className="px-5 py-2.5 text-slate-600 whitespace-nowrap">{formatDate(c.fecha_inicio)}</td>
                    <td className="px-5 py-2.5 text-slate-600 whitespace-nowrap">{formatDate(c.fecha_termino)}</td>
                    <td className="px-5 py-2.5 text-right text-slate-700 font-mono">{c.dotacion ?? <span className="text-slate-300">—</span>}</td>
                    <td className="px-5 py-2.5 text-slate-600">{c.gerencia || <span className="text-slate-300">—</span>}</td>
                    <td className="px-5 py-2.5 text-right">
                      <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">{countContrato(c.name)}</span>
                    </td>
                    {canEdit && (
                      <td className="px-5 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <button data-testid={`edit-contrato-${c.id}`} onClick={() => setContratoDialog({ contrato: c })}
                          className="text-slate-400 hover:text-indigo-600 mr-2" title="Editar"><Pencil size={14} /></button>
                        <button data-testid={`del-contrato-${c.id}`} onClick={(e) => removeContrato(e, c)}
                          className="text-slate-400 hover:text-red-600" title="Eliminar"><Trash2 size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Estándar de documentos del trabajador */}
        </TabsContent>

        <TabsContent value="documentos" className="mt-4">
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><FileCheck2 size={18} className="text-emerald-600" /> Estándar: Documentos del trabajador</h2>
          <p className="text-xs text-slate-400 mt-0.5">Columnas de la matriz. Cada documento puede ser requerido (indispensable para acreditarse), tener vencimiento, o aplicar solo con una condición.</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              <th className="px-5 py-2 w-10">#</th>
              <th className="px-5 py-2">Documento</th>
              <th className="px-5 py-2 whitespace-nowrap text-center w-28">Requerido</th>
              <th className="px-5 py-2 whitespace-nowrap text-center w-28">Vencimiento</th>
              <th className="px-5 py-2 whitespace-nowrap w-56">Condición</th>
              {canEdit && <th className="px-5 py-2 text-right w-20"></th>}
            </tr>
          </thead>
          <tbody>
            {docs.map((d, i) => {
              const isPasaporte = (d.code || "").toLowerCase().includes("pasaporte") || (d.name || "").toLowerCase().includes("pasaporte");
              return (
                <tr key={d.id} data-testid={`mandante-doc-${d.id}`} className="border-t border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-2.5 font-mono text-[11px] text-slate-400">{i + 1}</td>
                  <td className="px-5 py-2.5">
                    <InlineEdit testid={`mandante-doc-name-${d.id}`} value={d.name} disabled={!canEdit}
                      onSave={(name) => { updateDocAttrs(d, { name }); setDocs((p) => p.map((x) => x.id === d.id ? { ...x, name } : x)); toast.success("Documento renombrado"); }}
                      className="text-slate-700" />
                  </td>
                  <td className="px-5 py-2.5 text-center">
                    <Switch data-testid={`doc-required-${d.id}`}
                      checked={d.required !== false}
                      disabled={!canEdit}
                      onCheckedChange={(v) => updateDocAttrs(d, { required: v })} />
                  </td>
                  <td className="px-5 py-2.5 text-center">
                    <Switch data-testid={`doc-expiration-${d.id}`}
                      checked={!!d.has_expiration}
                      disabled={!canEdit}
                      onCheckedChange={(v) => updateDocAttrs(d, { has_expiration: v })} />
                  </td>
                  <td className="px-5 py-2.5 text-xs text-slate-500">
                    {canEdit ? (
                      <select data-testid={`doc-condition-${d.id}`}
                        value={d.condition || ""}
                        onChange={(e) => updateDocAttrs(d, { condition: e.target.value || null })}
                        disabled={!isPasaporte && !d.condition}
                        title={!isPasaporte && !d.condition ? "La condición 'visa temporaria' solo aplica para Pasaporte" : ""}
                        className="h-8 px-2 rounded-md border border-slate-200 bg-white text-xs disabled:bg-slate-50 disabled:text-slate-400">
                        <option value="">Siempre</option>
                        <option value="visa_temporaria">Solo con visa temporaria</option>
                      </select>
                    ) : (
                      d.condition === "visa_temporaria"
                        ? <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Solo con visa temporaria</span>
                        : "Siempre"
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-5 py-2.5 text-right">
                      <button data-testid={`del-mandante-doc-${d.id}`} onClick={() => removeDoc(d)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </td>
                  )}
                </tr>
              );
            })}
            {docs.length === 0 && <tr><td colSpan={6} className="px-5 py-4 text-sm text-slate-400 italic">Sin documentos definidos.</td></tr>}
          </tbody>
        </table>
        {canEdit && (
          <div className="flex gap-2 p-4 border-t border-slate-100">
            <input data-testid="new-doc-input" value={newDoc} onChange={(e) => setNewDoc(e.target.value)} placeholder="Nuevo documento requerido…"
              onKeyDown={(e) => e.key === "Enter" && addDoc()}
              className="flex-1 px-3 py-1.5 rounded-md border border-slate-200 focus:border-emerald-400 outline-none text-sm" />
            <button data-testid="add-doc-button" onClick={addDoc} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"><Plus size={13} /> Documento</button>
          </div>
        )}
      </section>

      {/* Cargos del mandante (mantenedor) */}
        </TabsContent>

        <TabsContent value="cargos" className="mt-4">
      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><Briefcase size={18} className="text-blue-600" /> Mantenedor de cargos</h2>
          <p className="text-xs text-slate-400 mt-0.5">Catálogo de posiciones del mandante. Se alimenta con la carga masiva (Conversor) y manualmente. Usa este listado al crear cargos del contrato.</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              <th className="px-5 py-2">Cargo</th>
              <th className="px-5 py-2 text-right whitespace-nowrap">Trabajadores</th>
              {canEdit && <th className="px-5 py-2 text-right w-20"></th>}
            </tr>
          </thead>
          <tbody>
            {cargos.map((cg) => (
              <tr key={cg.id} data-testid={`mandante-cargo-${cg.id}`} className="border-t border-slate-50 hover:bg-slate-50">
                <td className="px-5 py-2.5">
                  <InlineEdit testid={`mandante-cargo-name-${cg.id}`} value={cg.name} disabled={!canEdit} uppercase
                    onSave={(name) => renameCargo(cg, name)} className="text-slate-700" />
                </td>
                <td className="px-5 py-2.5 text-right">
                  <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">{countCargo(cg.name)}</span>
                </td>
                {canEdit && (
                  <td className="px-5 py-2.5 text-right">
                    <button data-testid={`del-mandante-cargo-${cg.id}`} onClick={() => removeCargo(cg)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
            {cargos.length === 0 && <tr><td colSpan={3} className="px-5 py-4 text-sm text-slate-400 italic">Sin cargos definidos. Agrega uno manual o importa trabajadores desde el Conversor.</td></tr>}
          </tbody>
        </table>
        {canEdit && (
          <div className="flex gap-2 p-4 border-t border-slate-100">
            <input data-testid="new-mandante-cargo-input" value={newCargo} onChange={(e) => setNewCargo(e.target.value.toUpperCase())} placeholder="Nuevo cargo…"
              onKeyDown={(e) => e.key === "Enter" && addCargo()}
              className="flex-1 px-3 py-1.5 rounded-md border border-slate-200 focus:border-blue-400 outline-none text-sm" />
            <button data-testid="add-mandante-cargo-button" onClick={addCargo} className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"><Plus size={13} /> Cargo</button>
          </div>
        )}
      </section>
        </TabsContent>

        <TabsContent value="workflow" className="mt-4">
          <WorkflowBuilder mandanteId={id} mandanteName={mandante.name} canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      <ContratoFormDialog open={!!contratoDialog} onOpenChange={(v) => { if (!v) setContratoDialog(null); }}
        initial={contratoDialog?.contrato || null} mandanteName={mandante.name}
        onSubmit={async (payload) => {
          if (contratoDialog?.contrato) await updateContrato(contratoDialog.contrato, payload);
          else await addContrato(payload);
        }} />

      {similarWarn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setSimilarWarn(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="similar-cargo-modal">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              <h3 className="font-display font-semibold text-slate-800">Cargo similar detectado</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600">Estás por agregar <span className="font-semibold text-slate-800">"{similarWarn.name}"</span>. Ya existen cargos parecidos:</p>
              <div className="flex flex-wrap gap-1.5">
                {similarWarn.similares.map((s) => (
                  <span key={s} className="inline-flex items-center text-xs font-medium bg-amber-50 text-amber-700 px-2 py-1 rounded">{s}</span>
                ))}
              </div>
              <p className="text-xs text-slate-500">Si es un cargo distinto, continúa. Si es duplicado, reutiliza el existente.</p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-100">
              <button onClick={() => setSimilarWarn(null)} className="h-9 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">Cancelar</button>
              <button data-testid="confirm-similar-cargo"
                onClick={async () => { const n = similarWarn.name; setSimilarWarn(null); await createCargo(n); }}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">
                Agregar igualmente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
